import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { verify2FA, resend2FA, fallbackEmail2FA, type RequiresTwoFactor, type User } from '../api/auth'

interface Props {
  challenge: RequiresTwoFactor
  onSuccess: (user: User) => void
  onCancel?: () => void
}

// Экран ввода кода 2FA при логине (ТЗ_admin_2fa.md §5.2.1) — используется и
// в Login.tsx, и в TelegramCallback.tsx: challenge уже создан бэкендом,
// здесь только ввод/переотправка/фолбэк на почту.
export default function TwoFactorCodeStep({ challenge, onSuccess, onCancel }: Props) {
  const { t } = useTranslation()
  const [current, setCurrent] = useState(challenge)
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!code) return
    setError('')
    setLoading(true)
    try {
      const user = await verify2FA(current.challenge_id, code)
      onSuccess(user)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.twofa.error'))
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setResending(true)
    setError('')
    try {
      await resend2FA(current.challenge_id)
      setResendCooldown(true)
      setTimeout(() => setResendCooldown(false), 60_000)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.twofa.error'))
    } finally {
      setResending(false)
    }
  }

  const handleFallbackEmail = async () => {
    setError('')
    setLoading(true)
    try {
      const next = await fallbackEmail2FA(current.challenge_id)
      setCurrent(next)
      setCode('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.twofa.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form" noValidate>
      <p className="auth-subtitle">
        {current.method === 'totp'
          ? t('auth.twofa.hintTotp')
          : current.hint
          ? t('auth.twofa.hintSent', { hint: current.hint })
          : t('auth.twofa.hintGeneric')}
      </p>

      {error && <div className="auth-error">{error}</div>}

      <div className="auth-input-wrapper">
        <input
          type="text"
          inputMode="numeric"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('auth.twofa.codePlaceholder')}
          className="auth-input"
        />
      </div>

      <button type="submit" className="auth-btn auth-btn-primary" disabled={loading || !code}>
        {loading ? t('auth.twofa.verifying') : t('auth.twofa.submit')}
      </button>

      <div className="auth-resend">
        {current.method !== 'totp' && (
          <button
            type="button"
            className="auth-btn auth-btn-secondary"
            onClick={handleResend}
            disabled={resending || resendCooldown}
          >
            {resendCooldown ? t('auth.twofa.resendCooldown') : t('auth.twofa.resend')}
          </button>
        )}
        {current.method !== 'email' && (
          <button type="button" className="auth-link" onClick={handleFallbackEmail} disabled={loading}>
            {t('auth.twofa.fallbackEmail')}
          </button>
        )}
      </div>

      {onCancel && (
        <button type="button" className="auth-link" onClick={onCancel}>
          {t('auth.twofa.cancel')}
        </button>
      )}
    </form>
  )
}
